import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface MembershipFeeCharge {
  id: string;
  virtualAccountId: string;
  competence: string;
  amount: string;
  status: string;
  ledgerEntryId: string | null;
  chargedAt: string | null;
  failureReason: string | null;
  accountHolder?: string;
  accountHolderEmail?: string;
}

export interface CompetenceSummary {
  competence: string;
  totalAccounts: number;
  totalCharged: number;
  totalPending: number;
  totalFailed: number;
  amountCharged: string;
  amountPending: string;
}

export interface BatchChargeResult {
  total: number;
  charged: number;
  pending: number;
  skipped: number;
  failed: number;
  details: MembershipFeeCharge[];
}

// Hooks
export function useMembershipFees(competence?: string, status?: string) {
  return useQuery({
    queryKey: ['membership-fees', competence, status],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (competence) params.set('competence', competence);
      if (status) params.set('status', status);
      params.set('take', '100');
      const { data } = await api.get<{ charges: MembershipFeeCharge[]; total: number }>(
        `/virtual-accounts/membership-fees?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useMembershipFeeSummary(competence: string) {
  return useQuery({
    queryKey: ['membership-fees', 'summary', competence],
    queryFn: async () => {
      const { data } = await api.get<CompetenceSummary>(
        `/virtual-accounts/membership-fees/summary?competence=${competence}`,
      );
      return data;
    },
    enabled: !!competence,
  });
}

export function useChargeMembershipFee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { virtualAccountId: string; competence: string }) => {
      const { data } = await api.post<MembershipFeeCharge>(
        '/virtual-accounts/membership-fees/charge',
        params,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membership-fees'] });
    },
  });
}

export function useChargeAllMembershipFees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { competence: string }) => {
      const { data } = await api.post<BatchChargeResult>(
        '/virtual-accounts/membership-fees/charge-all',
        params,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membership-fees'] });
    },
  });
}


// Fee config hooks
export interface MembershipFeeConfig {
  membershipFeeAmount: number;
  membershipFeeMinDepositThreshold: number;
  membershipFeeCronDay: number;
  membershipFeeAutoChargeEnabled: boolean;
  membershipFeeExemptTypes: string[];
  adhesionFee: number;
  adhesionFeeEnabled: boolean;
}

export function useMembershipFeeConfig() {
  return useQuery({
    queryKey: ['membership-fee-config'],
    queryFn: async () => {
      // Get current user's tenantId from auth
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Not authenticated');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tenantId = payload.tenantId;
      const { data } = await api.get<MembershipFeeConfig>(`/provider-engine/tenant-fee-config/${tenantId}`);
      return data;
    },
  });
}

export function useUpdateMembershipFeeConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: Partial<MembershipFeeConfig>) => {
      const token = localStorage.getItem('accessToken');
      if (!token) throw new Error('Not authenticated');
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tenantId = payload.tenantId;
      const { data } = await api.put(`/provider-engine/tenant-fee-config/${tenantId}`, params);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['membership-fee-config'] });
    },
  });
}

export function useUnchargedAccounts(competence: string) {
  return useQuery({
    queryKey: ['membership-fees', 'uncharged', competence],
    queryFn: async () => {
      const { data } = await api.get('/virtual-accounts/membership-fees/uncharged', {
        params: { competence },
      });
      return data as { total: number; accounts: Array<{ virtualAccountId: string; name: string; email: string; cpfCnpj: string; createdAt: string }> };
    },
    enabled: !!competence,
  });
}
